import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { v4 } from "uuid";

export const axiosClient = axios.create({ baseURL: "/api" });

axiosClient.interceptors.request.use((config) => {
  config.headers["X-Request-Id"] = v4();
  return config;
});

axiosRetry(axiosClient, {
  retries: 3,
  retryDelay: (retryCount, error) => {
    return axiosRetry.exponentialDelay(retryCount, error);
  },
  retryCondition: (error) => {
    // If we have a response with status 503, do NOT retry
    if (error.response?.status === 503) {
      return false;
    }

    // Otherwise, fall back to axios-retry’s default behavior
    return axiosRetry.isNetworkOrIdempotentRequestError(error);
  },
});

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown,
  ) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = "ApiError";
  }
}

export function parseError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    const apiError = new ApiError(
      `${error.message} - ErrorCode: ${error.code} - Url: ${error.config?.url} - Method: ${error.config?.method} - Retries: ${error.config?.["axios-retry"]?.retryCount}/${error.config?.["axios-retry"]?.retries}`,
      error.response?.status,
      error.response?.data,
    );
    if (axios.isCancel(error)) {
      apiError.name = "AbortError";
    }
    return apiError;
  }
  return new ApiError("Ukjent feil");
}
export async function get<DataOut>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<DataOut>> {
  try {
    return await axiosClient.get<DataOut>(url, config);
  } catch (error) {
    throw parseError(error);
  }
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export async function put<DataIn, DataOut>(
  url: string,
  data: DataIn,
  signal?: AbortSignal,
): Promise<AxiosResponse<DataOut>> {
  try {
    return await axiosClient.put<DataOut>(url, data, { signal });
  } catch (error) {
    throw parseError(error);
  }
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export async function post<DataIn, DataOut>(
  url: string,
  data?: DataIn,
): Promise<AxiosResponse<DataOut>> {
  try {
    return await axiosClient.post<DataOut>(url, data);
  } catch (error) {
    throw parseError(error);
  }
}

export async function deleteCustom(url: string): Promise<AxiosResponse<void>> {
  try {
    return await axiosClient.delete(url);
  } catch (error) {
    throw parseError(error);
  }
}
